import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import VendorScope3Form from "./VendorScope3Form";

const CATEGORIES = [
  { id: 1, label: "Purchased goods & services" },
  { id: 4, label: "Upstream transport" },
  { id: 5, label: "Waste treatment" },
  { id: 9, label: "Downstream transport" },
  { id: 10, label: "Processing of sold products" },
];

/**
 * Vendors — list, create, and sandbox template preview.
 */
const VendorsPage = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/vendors?limit=100");
      if (data.status && data.vendors) setVendors(data.vendors);
    } catch (e) {
      toastr.error(e.response?.data?.message || "Could not load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createVendor = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toastr.warning("Name and email are required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post("/api/vendors", { name: name.trim(), email: email.trim() });
      if (data.status && data.vendor) {
        toastr.success("Vendor created");
        setName("");
        setEmail("");
        navigate(`/vendors/${data.vendor.id}`);
      }
    } catch (err) {
      toastr.error(err.response?.data?.message || "Could not create vendor");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h1 className="h3 mb-1">Vendors</h1>
        <p className="text-muted mb-0">
          Add suppliers, assign disclosure categories by reporting year, and track responses.
        </p>
      </div>

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h2 className="h5 card-title">Add vendor</h2>
              <form onSubmit={createVendor}>
                <div className="mb-2">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Supplier name"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@supplier.com"
                  />
                </div>
                <button type="submit" className="btn btn-success" disabled={creating}>
                  {creating ? "Creating…" : "Create and open"}
                </button>
              </form>

              <hr className="my-4" />

              <h2 className="h6">Your vendors</h2>
              {loading ? (
                <p className="text-muted small">Loading…</p>
              ) : vendors.length === 0 ? (
                <p className="text-muted small">No vendors yet.</p>
              ) : (
                <div className="list-group">
                  {vendors.map((v) => (
                    <button
                      type="button"
                      key={v.id}
                      className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                      onClick={() => navigate(`/vendors/${v.id}`)}
                    >
                      <span>
                        <strong>{v.name}</strong>
                        <br />
                        <span className="small text-muted">{v.email}</span>
                      </span>
                      <span className="badge bg-secondary">{v.assignmentCount ?? 0} templates</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h5 card-title">Template preview (sandbox)</h2>
              <p className="small text-muted">
                Try disclosure forms without a vendor record. Categories: {CATEGORIES.map((c) => c.id).join(", ")}.
              </p>
              <VendorScope3Form />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorsPage;
